import { useRef, useCallback } from 'react';
import { TokenResponse } from '../types';

interface TokenCache {
    token: string;
    expiresAt: number;
}

/**
 * Hook to cache auth tokens and auto-refresh before expiry
 * Tokens are refreshed when <5 minutes remain
 */
export function useTokenCache(getToken: () => Promise<TokenResponse> | TokenResponse) {
    const cache = useRef<TokenCache | null>(null);

    const getCachedToken = useCallback(async (): Promise<string> => {
        const now = Date.now();

        // If token exists and has >5 minutes left, use cached
        if (cache.current && cache.current.expiresAt - now > 5 * 60 * 1000) {
            return cache.current.token;
        }

        // Fetch new token with expiry
        const tokenResponse = await getToken();

        // Cache token with expiry time
        cache.current = {
            token: tokenResponse.token,
            expiresAt: new Date(tokenResponse.expiresAt).getTime(),
        };

        return tokenResponse.token;
    }, [getToken]);

    return getCachedToken;
}
